import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import AdminHeader from '../../components/AdminHeader';
import './admin.css';

// ─── Toggle helper (matches AdminDashboard pattern) ───────────────────────────
function Toggle({ id, checked, onChange }) {
  return (
    <label className="a-toggle" htmlFor={id}>
      <input
        type="checkbox"
        id={id}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="a-toggle__slider" />
    </label>
  );
}

// ─── PlacementCategories ──────────────────────────────────────────────────────
export default function PlacementCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  // Form state: null = idle | 'adding' = new row | { item } = editing
  const [mode, setMode]         = useState(null);
  const [form, setForm]         = useState({ name: '', description: '', display_order: 0, is_active: true });
  const [formError, setFormError] = useState('');
  const [saving, setSaving]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/api/admin/placement-categories')
      .then((res) => setCategories(res.data.categories))
      .catch(() => setError('Failed to load placement categories.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startAdd() {
    setMode('adding');
    setForm({ name: '', description: '', display_order: 0, is_active: true });
    setFormError('');
  }

  function startEdit(cat) {
    setMode({ item: cat });
    setForm({
      name:          cat.name,
      description:   cat.description || '',
      display_order: cat.display_order,
      is_active:     cat.is_active,
    });
    setFormError('');
  }

  function cancelForm() {
    setMode(null);
    setFormError('');
  }

  async function handleSubmit() {
    if (!form.name.trim()) {
      setFormError('Name is required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (mode === 'adding') {
        await api.post('/api/admin/placement-categories', {
          ...form,
          name: form.name.trim(),
          description: form.description.trim() || null,
        });
      } else {
        await api.put(`/api/admin/placement-categories/${mode.item.id}`, {
          ...form,
          name: form.name.trim(),
          description: form.description.trim() || null,
        });
      }
      setMode(null);
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(cat) {
    try {
      await api.put(`/api/admin/placement-categories/${cat.id}`, {
        is_active: !cat.is_active,
      });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update category.');
    }
  }

  async function handleDelete(cat) {
    if (cat.usage_count > 0) {
      setError(
        `"${cat.name}" is used by ${cat.usage_count} evaluation(s) and cannot be deleted. Deactivate it instead.`
      );
      return;
    }
    if (!window.confirm(`Delete placement category "${cat.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/placement-categories/${cat.id}`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete category.');
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading && categories.length === 0) {
    return (
      <div className="c-page">
        <AdminHeader />
        <main className="c-main">
          <div className="c-loading">
            <div className="c-spinner" aria-label="Loading…" />
            <p>Loading placement categories…</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="c-page">
      <AdminHeader />

      <main className="c-main">

        {/* ── Page header ── */}
        <div className="a-results-header">
          <div>
            <Link to="/admin/dashboard" className="c-back-btn">← Back to Dashboard</Link>
            <h1 className="a-results-title">Placement Categories</h1>
          </div>
          {mode === null && (
            <button className="c-btn" onClick={startAdd}>+ Add Category</button>
          )}
        </div>

        <p style={{ color: '#6b7280', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
          Define placement tiers that judges assign to songs after submitting their evaluation.
          Lower display-order values appear first in the judge dropdown.
        </p>

        {/* ── Error banner ── */}
        {error && (
          <div className="c-alert" role="alert" style={{ marginBottom: '1rem' }}>
            {error}
            <button
              style={{ marginLeft: '1rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
              onClick={() => setError('')}
            >✕</button>
          </div>
        )}

        {/* ── Add / Edit form ── */}
        {mode !== null && (
          <div className="c-form-card" style={{ marginBottom: '1.5rem' }}>
            <div className="c-form-card__title">
              {mode === 'adding' ? 'New Placement Category' : `Edit — ${mode.item.name}`}
            </div>

            {formError && <div className="c-alert" style={{ marginBottom: '1rem' }}>{formError}</div>}

            <div className="c-form-grid">

              <div className="c-form-group">
                <label className="c-label">Name *</label>
                <input
                  className="c-input"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Winner, Runner-up, Best Original…"
                  autoFocus
                />
              </div>

              <div className="c-form-group">
                <label className="c-label">Display Order</label>
                <input
                  className="c-input"
                  type="number"
                  min="0"
                  value={form.display_order}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, display_order: Number(e.target.value) }))
                  }
                />
              </div>

              <div className="c-form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="c-label">Description</label>
                <input
                  className="c-input"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Optional description for this placement tier"
                />
              </div>

              <div className="a-toggle-row" style={{ gridColumn: '1 / -1' }}>
                <span className="c-label" style={{ margin: 0 }}>Active</span>
                <Toggle
                  id="pc-active"
                  checked={form.is_active}
                  onChange={(v) => setForm((p) => ({ ...p, is_active: v }))}
                />
              </div>

            </div>

            <div className="a-form-actions">
              <button className="c-btn" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="c-btn c-btn--outline" onClick={cancelForm}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Table ── */}
        {loading ? (
          <div className="c-loading"><div className="c-spinner" /></div>
        ) : categories.length === 0 ? (
          <div className="c-empty">
            <div className="c-empty__icon">🏆</div>
            <h3 className="c-empty__title">No placement categories yet</h3>
            <p className="c-empty__text">
              Add your first category and judges will be able to designate songs from their dashboard.
            </p>
            {mode === null && (
              <button className="c-btn" style={{ marginTop: '1rem' }} onClick={startAdd}>
                + Add Category
              </button>
            )}
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Order</th>
                  <th>In use</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.id}>
                    <td><strong>{cat.name}</strong></td>
                    <td style={{ color: cat.description ? 'inherit' : '#9ca3af' }}>
                      {cat.description || '—'}
                    </td>
                    <td>{cat.display_order}</td>
                    <td>
                      <span style={{ color: cat.usage_count > 0 ? '#1a1a2e' : '#9ca3af' }}>
                        {cat.usage_count} evaluation{cat.usage_count !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${cat.is_active ? 'badge--active' : 'badge--inactive'}`}>
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="a-row-actions">
                        <button
                          className="c-btn c-btn--sm c-btn--outline"
                          onClick={() => startEdit(cat)}
                        >
                          Edit
                        </button>
                        <button
                          className="c-btn c-btn--sm c-btn--outline"
                          onClick={() => toggleActive(cat)}
                        >
                          {cat.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="c-btn c-btn--sm c-btn--danger"
                          onClick={() => handleDelete(cat)}
                          disabled={cat.usage_count > 0}
                          title={
                            cat.usage_count > 0
                              ? `In use by ${cat.usage_count} evaluation(s) — deactivate instead`
                              : 'Delete this category'
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: '32px' }}>
          <Link to="/admin/dashboard" className="c-back-btn">← Back to Dashboard</Link>
        </div>

      </main>
    </div>
  );
}
